import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { IconDownload } from './Icons';
import { MOCK_PROPOSALS } from '../data/tendersMock';
import WorkflowIndicator from './WorkflowIndicator';
import './Tenders.css';

function getStatusBadgeClass(status) {
  const s = (status || '').toLowerCase().replace(/\s+/g, '-');
  if (s === 'draft') return 'tenders-badge-draft';
  if (s === 'submitted') return 'tenders-badge-submitted';
  if (s === 'under-evaluation') return 'tenders-badge-under-review';
  if (s === 'awarded') return 'tenders-badge-awarded';
  if (s === 'lost') return 'tenders-badge-lost';
  return 'tenders-badge-draft';
}

/** Map proposal status to workflow stage */
function proposalStatusToWorkflowStage(status) {
  const s = (status || '').toLowerCase();
  if (s === 'draft') return 'Proposal Prepared';
  if (s === 'submitted') return 'Submitted';
  if (s === 'under evaluation') return 'Evaluation';
  if (s === 'awarded') return 'Awarded';
  if (s === 'lost') return 'Evaluation';
  return 'Proposal Prepared';
}

const MOCK_PROPOSAL_DOCS = [
  { id: 'pd1', filename: 'Technical_Submission.pdf', documentType: 'Technical Proposal', version: '1', uploadDate: '2026-02-20', uploadedBy: 'Ahmed Al-Rashdi' },
  { id: 'pd2', filename: 'Commercial_Offer.pdf', documentType: 'Commercial Proposal', version: '1', uploadDate: '2026-02-20', uploadedBy: 'Ahmed Al-Rashdi' },
];

export default function ProposalDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');

  const proposal = MOCK_PROPOSALS.find((p) => p.id === id);
  const workflowStage = proposal ? proposalStatusToWorkflowStage(proposal.status) : null;

  const canSubmit = proposal && proposal.status === 'Draft';
  const canMarkSubmitted = proposal && proposal.status === 'Draft';
  const canSendForApproval = proposal && proposal.approved === 'No';

  if (!proposal) {
    return (
      <div className="tenders-details-page">
        <div className="tenders-details-card">
          <p role="alert">Proposal not found.</p>
          <button type="button" className="tenders-footer-btn" onClick={() => navigate('/tenders/proposals')}>Back to Proposals</button>
        </div>
      </div>
    );
  }

  const tabs = [
    { key: 'overview', label: 'Overview' },
    { key: 'financial', label: 'Financial' },
    { key: 'technical', label: 'Technical' },
    { key: 'documents', label: 'Documents' },
    { key: 'submission', label: 'Submission' },
    { key: 'result', label: 'Result' },
  ];

  return (
    <div className="tenders-details-page">
      <div className="tenders-details-card">
        <button type="button" className="tenders-details-back" onClick={() => navigate('/tenders/proposals')}>
          ← Back to Proposals
        </button>
        <h1 className="tenders-title" style={{ marginBottom: '0.5rem' }}>{proposal.proposalId}</h1>
        <p style={{ fontSize: '0.9rem', color: 'var(--text-muted)', marginBottom: '1rem' }}>Linked: {proposal.tenderRef} — {proposal.tenderTitle}</p>

        <WorkflowIndicator currentStage={workflowStage} />

        <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', marginBottom: '1.25rem' }}>
          <button type="button" className="tenders-header-btn primary" disabled={!canSubmit} title={!canSubmit ? 'Available when status is Draft' : ''}>
            Submit Proposal
          </button>
          <button type="button" className="tenders-header-btn" disabled={!canSendForApproval} title={!canSendForApproval ? 'Already approved or N/A' : ''}>
            Send for Approval
          </button>
          <button type="button" className="tenders-header-btn" disabled={!canMarkSubmitted} title={!canMarkSubmitted ? 'Available when status is Draft' : ''}>
            Mark as Submitted
          </button>
        </div>

        <div className="tenders-details-tabs">
          {tabs.map((tab) => (
            <button key={tab.key} type="button" className={`tenders-details-tab ${activeTab === tab.key ? 'active' : ''}`} onClick={() => setActiveTab(tab.key)}>
              {tab.label}
            </button>
          ))}
        </div>

        {activeTab === 'overview' && (
          <div className="tenders-info-card">
            <div className="tenders-info-grid">
              <div className="tenders-info-item">
                <label>Client</label>
                <span>{proposal.client}</span>
              </div>
              <div className="tenders-info-item">
                <label>Proposal Version</label>
                <span>{proposal.proposalVersion}</span>
              </div>
              <div className="tenders-info-item">
                <label>Proposal Type</label>
                <span>{proposal.proposalType}</span>
              </div>
              <div className="tenders-info-item">
                <label>Status</label>
                <span className={`tenders-badge ${getStatusBadgeClass(proposal.status)}`}>{proposal.status}</span>
              </div>
              <div className="tenders-info-item">
                <label>Approved</label>
                <span className={`tenders-badge ${proposal.approved === 'Yes' ? 'tenders-badge-approved' : 'tenders-badge-not-approved'}`}>{proposal.approved}</span>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'financial' && (
          <div className="tenders-info-card">
            <div className="tenders-info-grid">
              <div className="tenders-info-item">
                <label>Proposed Amount</label>
                <span>{proposal.proposedAmount != null ? `${proposal.proposedAmount?.toLocaleString()} ${proposal.currency || 'OMR'}` : '—'}</span>
              </div>
              {proposal.awardAmount != null && (
                <div className="tenders-info-item">
                  <label>Award Amount</label>
                  <span>{proposal.awardAmount?.toLocaleString()} {proposal.currency || 'OMR'}</span>
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'technical' && (
          <div className="tenders-info-card">
            <p style={{ color: 'var(--text-muted)', fontSize: '0.9rem' }}>Scope, method statement, resources (mock).</p>
          </div>
        )}

        {activeTab === 'documents' && (
          <>
            <div className="tenders-upload-zone">Drop files here or click to upload (UI only).</div>
            <ul className="tenders-doc-list">
              {MOCK_PROPOSAL_DOCS.map((doc) => (
                <li key={doc.id} className="tenders-doc-item">
                  <span>{doc.filename}</span>
                  <span style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>{doc.documentType} · {doc.uploadDate}</span>
                  <button type="button" className="tenders-row-icon-btn" aria-label="Download"><IconDownload /></button>
                </li>
              ))}
            </ul>
          </>
        )}

        {activeTab === 'submission' && (
          <div className="tenders-info-card">
            <div className="tenders-info-grid">
              <div className="tenders-info-item">
                <label>Submission Date</label>
                <span>{proposal.submissionDate || '—'}</span>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'result' && (
          <div className="tenders-info-card">
            <div className="tenders-info-grid">
              <div className="tenders-info-item">
                <label>Proposal Status</label>
                <span className={`tenders-badge ${getStatusBadgeClass(proposal.status)}`}>{proposal.status}</span>
              </div>
              {proposal.awardAmount != null && (
                <div className="tenders-info-item">
                  <label>Award Amount</label>
                  <span>{proposal.awardAmount?.toLocaleString()} {proposal.currency || 'OMR'}</span>
                </div>
              )}
              {proposal.lossReason && (
                <div className="tenders-info-item" style={{ gridColumn: '1 / -1' }}>
                  <label>Loss Reason</label>
                  <span>{proposal.lossReason}</span>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
